import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { mdiAccountOutline, mdiHelpCircleOutline } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";

import Banner from "components/Banner";
import { routes } from "constants/routes";
import useDeviceType from "hooks/useDeviceType";
import useNetworkStatus from "hooks/useNetworkStatus";
import { RootState } from "store";
import MobileNavigationBar from "./MobileNavigationBar";
import styles from "./style.module.scss";
import { useAnalytics } from "hooks/useAnalytics";

interface IProps {
  desktopTitle?: string;
  title?: string;
  subtitle?: string;
  customHeader?: React.ReactNode;
  hideSubHeader?: boolean;
  children: React.ReactNode;
  className?: string;
  containerClass?: string;
  hideNavigation?: boolean;
}

export default function MainLayout({
  desktopTitle,
  title,
  subtitle,
  customHeader,
  hideSubHeader = false,
  children,
  className,
  containerClass,
  hideNavigation = false
}: Readonly<IProps>) {
  const navigate = useNavigate();
  const isOnline = useNetworkStatus();
  const { isIOS, isAndroid, isDesktop } = useDeviceType();

  const { account, userToken } = useSelector((state: RootState) => state.authState);
  const { userDetails } = useSelector((state: RootState) => state.userState);
  const userType = account?.type;
  const businessName = userDetails?.dba;
  const isNewFrontendEnabled = userDetails?.frontend_settings?.enable_new_frontend;
  const { trackAllEvents } = useAnalytics();

  const goToUserSettings = () => {
    navigate(routes.SETTINGS);
  };

  const goToHelp = () => {
    navigate(routes.SETTINGS_HELP);
  };

  return (
    <>
      <div className={clsx(styles.mainContent, containerClass, title === "Home" && "!bg-accent")}>
        {isNewFrontendEnabled && (
          <Banner
            centerText
            className="sticky top-0 z-20"
            message={
              <p>
                Use the old app{" "}
                <a
                  href={`${process.env.REACT_APP_OLD_HAULPAY_APP_URL}/login?as=${userToken}`}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackAllEvents("banner_goto_oldapp")}
                  className="underline btn-link inlineviewlink text-blue-950">
                  here
                </a>
                . Please note that we will be retiring the old app in a few weeks.
              </p>
            }
          />
        )}
        <div
          className={clsx(styles.header, {
            "!top-0": !isNewFrontendEnabled,
            "!pt-14": isIOS && !isNewFrontendEnabled,
            "!pt-10": isAndroid && !isNewFrontendEnabled,
            "bg-gradient-to-r mobile:from-card mobile:to-foreground": title === "Home"
          })}>
          <div className={styles.desktopHeader}>
            <h3 className={styles.desktopTitle}>{desktopTitle}</h3>
            <div className="flex flex-col items-end justify-end">
              {desktopTitle === "Settings" ? (
                <button
                  className={clsx(isOnline ? "" : "border border-error rounded-full !w-fit")}
                  onClick={goToHelp}>
                  <Icon path={mdiHelpCircleOutline} className="flex-none size-6" />
                </button>
              ) : (
                <button
                  className={clsx(styles.accountCard, isOnline ? "" : "border border-error")}
                  onClick={goToUserSettings}>
                  <div className={styles.avatarWrapper}>
                    <Icon path={mdiAccountOutline} className="m-auto size-6" />
                  </div>
                  <div className={styles.contactName}>
                    <span className="whitespace-pre">{businessName}</span>
                    <span className="text-xs text-placeholder">{userType}</span>
                  </div>
                </button>
              )}
              {!isOnline ? (
                <span className="font-bold text-error">Alert! You are currently offline</span>
              ) : null}
            </div>
          </div>

          {customHeader ??
            (!hideSubHeader ? (
              <div className={clsx(styles.subHeader, "desktop:!hidden")}>
                <div className="flex flex-col">
                  <h3 className={styles.logoTitle}>{title ?? "Finance"}</h3>
                  <span className={styles.subtitle}>{subtitle}</span>
                </div>
                <button
                  className={clsx(
                    styles.accountButton,
                    isOnline ? "" : "border border-error rounded-full"
                  )}
                  onClick={goToUserSettings}>
                  <Icon path={mdiAccountOutline} />
                </button>
              </div>
            ) : null)}
        </div>

        <div className={clsx(styles.mainWrapper, className)}>{children}</div>
      </div>

      {!hideNavigation && !isDesktop && <MobileNavigationBar />}
    </>
  );
}
